import React, { useState, useEffect } from "react";
import { Trash2, X, Plus } from "lucide-react";
import FieldConfigurationAPI from "../../../../lib/api/FieldConfiguration";
import SearchableCodeSelect from "../../../common/SearchableCodeSelect";

function blankRule() {
  return {
    filter_type: "include",
    field_logs: {
      from_type: "",
      to_type: "",
      from_date: "",
      include_blank_type: true,
    },
  };
}

export default function FieldLogsFilterModal({
  open,
  groupUuid,
  companyUuid,
  filter,
  onSave,
  onClose,
  readOnly = false,
}) {
  const [rules, setRules] = useState([blankRule()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    const existing = filter?.rules;
    setRules(
      existing && existing.length > 0
        ? existing.map((r) => ({
            filter_type: r.filter_type || "include",
            field_logs: {
              from_type: r.field_logs?.from_type ?? "",
              to_type: r.field_logs?.to_type ?? "",
              from_date: r.field_logs?.from_date ?? "",
              include_blank_type: r.field_logs?.include_blank_type ?? true,
            },
          }))
        : [blankRule()]
    );
    setError(null);
  }, [open, filter]);

  if (!open) return null;

  function updateRule(index, field, value) {
    setRules((prev) =>
      prev.map((r, i) =>
        i === index
          ? { ...r, field_logs: { ...r.field_logs, [field]: value } }
          : r
      )
    );
  }

  function updateFilterType(index, value) {
    setRules((prev) =>
      prev.map((r, i) => (i === index ? { ...r, filter_type: value } : r))
    );
  }

  function removeRule(index) {
    setRules((prev) =>
      prev.length === 1 ? [blankRule()] : prev.filter((_, i) => i !== index)
    );
  }

  function handleSave() {
    const cleanedRules = rules.map((r) => ({
      filter_type: r.filter_type,
      field_logs: {
        from_type: r.field_logs.from_type || null,
        to_type: r.field_logs.to_type || null,
        from_date: r.field_logs.from_date || null,
        include_blank_type: !!r.field_logs.include_blank_type,
      },
    }));

    onSave({ resource: "field_logs", mode: "filtered", rules: cleanedRules });
  }

  return (
    <div className="jfm-overlay" onClick={onClose}>
      <div className="jfm-container" onClick={(e) => e.stopPropagation()}>
        <div className="jfm-header">
          <span className="jfm-title">Field Logs Filters</span>
          <button className="jfm-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="jfm-body">
          <fieldset className="jfm-fieldset" disabled={readOnly}>
            {rules.map((rule, idx) => (
              <div key={idx} className="efm-row">
                <select
                  className="jfm-type-select"
                  value={rule.filter_type}
                  onChange={(e) => updateFilterType(idx, e.target.value)}
                >
                  <option value="include">Include</option>
                  <option value="exclude">Exclude</option>
                </select>

                <div className="efm-fields">
                  <div className="efm-inputs-row">
                    <div className="efm-field">
                      <label>From Type</label>
                      <SearchableCodeSelect
                        value={rule.field_logs.from_type}
                        companyUuid={companyUuid}
                        placeholder="(first)"
                        fetchOptions={FieldConfigurationAPI.getFieldLogTypes}
                        emptyMessage="No field log types found"
                        disabled={readOnly}
                        onChange={(code) => updateRule(idx, "from_type", code)}
                      />
                    </div>
                    <div className="efm-field">
                      <label>To Type</label>
                      <SearchableCodeSelect
                        value={rule.field_logs.to_type}
                        companyUuid={companyUuid}
                        placeholder="(last)"
                        fetchOptions={FieldConfigurationAPI.getFieldLogTypes}
                        emptyMessage="No field log types found"
                        disabled={readOnly}
                        onChange={(code) => updateRule(idx, "to_type", code)}
                      />
                    </div>
                  </div>
                  <div className="efm-inputs-row">
                    <div className="efm-field">
                      <label>From Date</label>
                      <input
                        type="date"
                        value={rule.field_logs.from_date}
                        onChange={(e) =>
                          updateRule(idx, "from_date", e.target.value)
                        }
                      />
                    </div>
                    <div className="efm-field jfm-field--checkbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={rule.field_logs.include_blank_type}
                          onChange={(e) =>
                            updateRule(
                              idx,
                              "include_blank_type",
                              e.target.checked
                            )
                          }
                        />
                        Include Blank Type
                      </label>
                    </div>
                  </div>
                </div>

                {!readOnly && (
                  <button
                    className="jfm-delete"
                    onClick={() => removeRule(idx)}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
          </fieldset>
        </div>

        {error && <div className="jfm-error">{error}</div>}

        <div className="jfm-footer">
          {readOnly ? (
            <div />
          ) : (
            <button
              className="jfm-add-row"
              onClick={() => setRules((prev) => [...prev, blankRule()])}
            >
              <Plus size={14} /> Add Row
            </button>
          )}
          <div className="jfm-footer-actions">
            {readOnly ? (
              <button className="jfm-cancel" onClick={onClose}>
                Close
              </button>
            ) : (
              <>
                <button
                  className="jfm-cancel"
                  onClick={onClose}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  className="jfm-save"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
